if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.quantityInput = this.querySelector('[data-sticky-quantity-input]');
        this.nativeButton = document.querySelector(this.dataset.nativeSubmitSelector);
        this.nativeQuantityInput = document.querySelector(this.dataset.nativeQuantitySelector);
        this.nativePrice = document.querySelector(this.dataset.nativePriceSelector);
        this.stickyPrice = this.querySelector('.sticky-add-to-cart__price');
        this.mobileQuery = window.matchMedia('(max-width: 989px)');
        this.nativeButtonVisible = false;
        this.hasScrolledPastNativeButton = false;
        this.boundUpdateVisibility = this.updateVisibility.bind(this);
        this.boundRequestVisibilityUpdate = this.requestVisibilityUpdate.bind(this);
        this.visibilityFrame = null;
      }

      connectedCallback() {
        if (!this.nativeButton || !this.submitButton) return;

        this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        this.quantityInput?.addEventListener('change', this.syncQuantityToNative.bind(this));
        this.quantityInput?.addEventListener('input', this.syncQuantityToNative.bind(this));
        this.nativeQuantityInput?.addEventListener('change', this.syncQuantityFromNative.bind(this));
        this.nativeQuantityInput?.addEventListener('input', this.syncQuantityFromNative.bind(this));

        this.syncButtonState();
        this.syncButtonStyle();
        this.syncPrice();
        this.observeNativeButton();
        this.observeButtonState();
        this.observePrice();

        window.addEventListener('scroll', this.boundRequestVisibilityUpdate, { passive: true });
        this.mobileQuery.addEventListener('change', this.boundRequestVisibilityUpdate);
        this.updateVisibility();
      }

      onSubmit() {
        this.syncQuantityToNative();
        this.nativeButton.click();
      }

      syncQuantityToNative() {
        if (!this.quantityInput || !this.nativeQuantityInput) return;
        this.nativeQuantityInput.value = this.quantityInput.value;
        this.nativeQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncQuantityFromNative() {
        if (!this.quantityInput || !this.nativeQuantityInput) return;
        this.quantityInput.value = this.nativeQuantityInput.value;
      }

      syncButtonState() {
        const nativeText = this.nativeButton.querySelector('span')?.textContent?.trim();
        const stickyText = this.submitButton.querySelector('span');

        this.submitButton.toggleAttribute('disabled', this.nativeButton.hasAttribute('disabled'));

        if (nativeText && stickyText) stickyText.textContent = nativeText;
      }

      syncButtonStyle() {
        const nativeStyle = window.getComputedStyle(this.nativeButton);

        this.submitButton.style.backgroundColor = nativeStyle.backgroundColor;
        this.submitButton.style.color = nativeStyle.color;
        this.submitButton.style.borderColor = nativeStyle.borderColor;
      }

      syncPrice() {
        if (!this.nativePrice || !this.stickyPrice) return;
        this.stickyPrice.innerHTML = this.nativePrice.innerHTML;
      }

      observeButtonState() {
        this.buttonObserver = new MutationObserver(() => {
          this.syncButtonState();
          this.syncButtonStyle();
        });
        this.buttonObserver.observe(this.nativeButton, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      observePrice() {
        if (!this.nativePrice || !this.stickyPrice) return;

        this.priceObserver = new MutationObserver(this.syncPrice.bind(this));
        this.priceObserver.observe(this.nativePrice, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      observeNativeButton() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.nativeButtonVisible = entries[0]?.isIntersecting || false;
            this.requestVisibilityUpdate();
          },
          { threshold: 0.2 }
        );

        this.intersectionObserver.observe(this.nativeButton);
      }

      updateVisibility() {
        this.visibilityFrame = null;
        const nativeRect = this.nativeButton.getBoundingClientRect();
        const mobileImmediate = this.dataset.mobileImmediate === 'true';
        const isMobile = this.mobileQuery.matches;

        this.hasScrolledPastNativeButton = nativeRect.bottom < 0;

        let visible = this.hasScrolledPastNativeButton && !this.nativeButtonVisible;

        if (isMobile && mobileImmediate) {
          visible = !this.nativeButtonVisible;
        }

        this.classList.toggle('is-visible', visible);
        document.documentElement.classList.toggle('has-sticky-add-to-cart', visible);
      }

      requestVisibilityUpdate() {
        if (this.visibilityFrame) return;
        this.visibilityFrame = window.requestAnimationFrame(this.boundUpdateVisibility);
      }

      disconnectedCallback() {
        if (this.visibilityFrame) {
          window.cancelAnimationFrame(this.visibilityFrame);
        }
        this.intersectionObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
        window.removeEventListener('scroll', this.boundRequestVisibilityUpdate);
        this.mobileQuery.removeEventListener('change', this.boundRequestVisibilityUpdate);
        document.documentElement.classList.remove('has-sticky-add-to-cart');
      }
    }
  );
}
